import { useEffect, useMemo, useState } from 'react';
import { Users, User, Home, LayoutDashboard, BarChart3, FileText, Building2, CheckCircle, CreditCard, Percent, DollarSign, Headphones, AlertTriangle, UserCog, Key, Mail, Send, FolderOpen, ScrollText, Settings, Tag } from 'lucide-react';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import api from '@/services/api';
import { navigate } from '@/services/navigation';

const P = '/portal-8k2m9x';
const PAGES = [
  ['Home', `${P}/dashboard`, LayoutDashboard], ['Analytics', `${P}/analytics`, BarChart3], ['Landlords', `${P}/landlords`, Users],
  ['Properties', `${P}/properties`, Home], ['Tenants', `${P}/tenants`, User], ['Reports', `${P}/reports`, FileText],
  ['Tenancies', `${P}/tenancies`, Building2], ['Consent Requests', `${P}/consent-requests`, CheckCircle],
  ['Subscriptions', `${P}/subscriptions`, CreditCard], ['Discounts', `${P}/discounts`, Percent], ['Promo Codes', `${P}/promo-codes`, Tag], ['Payments', `${P}/payments`, DollarSign],
  ['Tickets', `${P}/support-tickets`, Headphones], ['Disputes', `${P}/disputes`, AlertTriangle], ['Users', `${P}/users`, UserCog],
  ['Teams', `${P}/teams`, Users], ['API Keys', `${P}/api-keys`, Key], ['Emails', `${P}/email-templates`, Mail],
  ['Newsletter', `${P}/newsletter`, Send], ['Client Docs', `${P}/client-documents`, FolderOpen], ['Audit', `${P}/audit-logs`, ScrollText],
  ['Settings', `${P}/settings`, Settings],
];
const ITEM = 'data-[selected=true]:bg-slate-100 data-[selected=true]:text-slate-900 cursor-pointer';

export const AdminCommandPalette = ({ open, onOpenChange }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); onOpenChange(!open); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open || data) return;
    Promise.all([api.get('/admin/verifications/landlords?status=all&limit=100'), api.get('/admin/users?limit=100'), api.get('/admin/properties/all?limit=100')])
      .then(([l, u, p]) => setData({ landlords: l.data.items || l.data || [], users: u.data.items || u.data || [], properties: p.data.items || p.data || [] }))
      .catch(() => setData({ landlords: [], users: [], properties: [] }));
  }, [open, data]);

  const go = (href) => { onOpenChange(false); navigate(href); };
  const landlords = useMemo(() => (data?.landlords || []).slice(0, 150), [data]);
  const users = useMemo(() => (data?.users || []).slice(0, 150), [data]);
  const properties = useMemo(() => (data?.properties || []).slice(0, 150), [data]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search landlords, users, properties, pages…" data-testid="admin-command-input" />
      <CommandList data-testid="admin-command-list">
        <CommandEmpty>{data ? 'No results.' : 'Loading…'}</CommandEmpty>
        <CommandGroup heading="Pages">
          {PAGES.map(([label, href, Icon]) => (
            <CommandItem key={href} value={`page ${label}`} className={ITEM} onSelect={() => go(href)} data-testid={`admin-cmd-page-${label.toLowerCase().replace(/\s/g, '-')}`}>
              <Icon className="w-4 h-4 mr-2 text-slate-400" />{label}
            </CommandItem>
          ))}
        </CommandGroup>
        {landlords.length > 0 && (
          <CommandGroup heading="Landlords">
            {landlords.map((l) => (
              <CommandItem key={l.id} value={`landlord ${l.legal_name || ''} ${l.email || ''} ${l.account_number || ''} ${l.id}`} className={ITEM} onSelect={() => go(`${P}/landlords`)}>
                <Users className="w-4 h-4 mr-2 text-slate-400" /><span className="flex-1 truncate">{l.legal_name || l.email}</span><span className="text-[11px] text-slate-400 ml-2 truncate">{l.email}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {users.length > 0 && (
          <CommandGroup heading="Users">
            {users.map((u) => (
              <CommandItem key={u.id} value={`user ${u.email} ${u.role || ''} ${u.id}`} className={ITEM} onSelect={() => go(`${P}/users`)}>
                <User className="w-4 h-4 mr-2 text-slate-400" /><span className="flex-1 truncate">{u.email}</span><span className="text-[11px] text-slate-400 ml-2">{u.role}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {properties.length > 0 && (
          <CommandGroup heading="Properties">
            {properties.map((p) => (
              <CommandItem key={p.id} value={`property ${p.address?.street || p.street_address || ''} ${p.address?.city || p.city || ''} ${p.landlord_name || ''} ${p.id}`} className={ITEM} onSelect={() => go(`${P}/properties`)}>
                <Home className="w-4 h-4 mr-2 text-slate-400" /><span className="flex-1 truncate">{p.address?.street || p.street_address}</span><span className="text-[11px] text-slate-400 ml-2">{p.address?.city || p.city}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};
